import React, { useEffect, useRef, useState, useCallback } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box, Card, CardContent, Typography, Button, Chip, Grid, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, useTheme,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopIcon from '@mui/icons-material/Stop';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { api, type Defect, type DetectionResult } from '../services/api';
import DefectOverlay from '../components/DefectOverlay';
import LatestCaptureCard from '../components/LatestCaptureCard';
import { getSeverityStyles } from '../components/ui/severity';

export default function ScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [inspectionId, setInspectionId] = useState<string | null>(null);
  const [defects, setDefects] = useState<Defect[]>([]);
  const [analysing, setAnalysing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [captureCount, setCaptureCount] = useState(0);
  const [lastDetections, setLastDetections] = useState<DetectionResult[]>([]);
  const theme = useTheme();

  // Inspection creation dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');

  useEffect(() => {
    if (!projectId) { toast.error('No project selected'); navigate('/projects'); }
  }, [projectId, navigate]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const startCamera = useCallback(async (facing: 'user' | 'environment' = facingMode) => {
    stopCamera();
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera API not available. Use http://localhost or HTTPS.');
      return;
    }
    const tryGet = (constraints: MediaStreamConstraints) =>
      navigator.mediaDevices.getUserMedia(constraints);
    try {
      let stream: MediaStream;
      try {
        stream = await tryGet({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (firstErr: any) {
        console.warn('Camera request with facingMode failed, retrying without it:', firstErr);
        if (firstErr?.name === 'OverconstrainedError' || firstErr?.name === 'NotFoundError') {
          stream = await tryGet({ video: true, audio: false });
        } else {
          throw firstErr;
        }
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
      setCapturedImage(null);
      setLastDetections([]);
    } catch (err: any) {
      console.error('getUserMedia failed:', err);
      let videoDeviceCount = -1;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        videoDeviceCount = devices.filter(d => d.kind === 'videoinput').length;
        console.info('enumerateDevices video inputs:', videoDeviceCount, devices);
      } catch (enumErr) {
        console.warn('enumerateDevices failed:', enumErr);
      }
      const notFoundHint = videoDeviceCount === 0
        ? 'No camera detected. On macOS: System Settings → Privacy & Security → Camera, enable your browser, then quit and reopen it.'
        : `No camera detected by the browser, but ${videoDeviceCount} video input(s) exist. Try granting permission again.`;
      const msg =
        err?.name === 'NotAllowedError' ? 'Camera permission denied. Allow camera access in your browser.' :
        err?.name === 'NotFoundError' ? notFoundHint :
        err?.name === 'NotReadableError' ? 'Camera is in use by another app.' :
        err?.name === 'OverconstrainedError' ? 'No camera matched the requested settings.' :
        err?.name === 'SecurityError' ? 'Camera blocked. Site must be on HTTPS or localhost.' :
        `Could not access camera (${err?.name || 'unknown'}).`;
      toast.error(msg, { duration: 8000 });
    }
  }, [facingMode]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const flipCamera = async () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    if (cameraActive) await startCamera(next);
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(dataUrl);
    stopCamera();

    // Auto-analyze if we have an inspection
    if (inspectionId) analyzeFromDataUrl(dataUrl);
  };

  const analyzeFromDataUrl = async (dataUrl: string) => {
    if (!inspectionId) return;
    setAnalysing(true);
    setLastDetections([]);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const result = await api.analyzeImage(inspectionId, file);
      setLastDetections(result.detections || []);
      setCaptureCount(prev => prev + 1);
      toast.success(`${result.defects_created} defect(s) detected`);
      const defs = await api.getDefects(inspectionId);
      setDefects(defs);
    } catch {
      toast.error('Analysis failed');
    } finally {
      setAnalysing(false);
    }
  };

  const analyzeFromFile = useCallback((file: File) => {
    if (!inspectionId) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCapturedImage(reader.result as string);
      stopCamera();
    };
    reader.readAsDataURL(file);

    setAnalysing(true);
    setLastDetections([]);
    api.analyzeImage(inspectionId, file)
      .then(result => {
        setLastDetections(result.detections || []);
        setCaptureCount(prev => prev + 1);
        toast.success(`${result.defects_created} defect(s) detected`);
        return api.getDefects(inspectionId);
      })
      .then(setDefects)
      .catch(() => toast.error('Analysis failed'))
      .finally(() => setAnalysing(false));
  }, [inspectionId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    disabled: !inspectionId || analysing,
    onDrop: (files) => {
      const file = files[0];
      if (file) analyzeFromFile(file);
    },
  });

  const createInspectionAndStart = async () => {
    if (!projectId || !title.trim()) return;
    try {
      const insp = await api.createInspection({
        project_id: projectId,
        title: title.trim(),
        location_description: location.trim() || undefined,
      });
      setInspectionId(insp.id);
      setDialogOpen(false);
      toast.success('Inspection created');
      await startCamera();
    } catch {
      toast.error('Failed to create inspection');
    }
  };

  const confirmDefect = async (defect: Defect, confirmed: boolean) => {
    try {
      const updated = await api.confirmDefect(defect.id, confirmed);
      setDefects(prev => prev.map(d => d.id === updated.id ? updated : d));
      toast.success(confirmed ? 'Defect confirmed' : 'Defect rejected');
    } catch { toast.error('Could not update defect'); }
  };

  const finishScan = () => {
    stopCamera();
    if (inspectionId) navigate(`/report/${inspectionId}`);
    else navigate('/projects');
  };

  const stats = {
    total: defects.length,
    confirmed: defects.filter(d => d.is_confirmed).length,
    critical: defects.filter(d => d.severity === 'critical').length,
    high: defects.filter(d => d.severity === 'high').length,
  };

  // Step 1: No inspection yet — prompt to create one
  if (!inspectionId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 3 }}>New Scan</Typography>
        <Card sx={{ borderRadius: 3, maxWidth: 500 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CameraAltIcon sx={{ color: 'primary.main', fontSize: 56, mb: 2 }} />
            <Typography sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Start a New Inspection</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>
              Create an inspection to begin scanning surfaces for defects using your camera.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)} sx={{ px: 4 }}>
              Create Inspection
            </Button>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}
          PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}`, minWidth: 400 } }}>
          <DialogTitle sx={{ color: 'text.primary' }}>New Inspection</DialogTitle>
          <DialogContent>
            <TextField label="Title" fullWidth required value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. West Wall - 3rd Floor"
              sx={{ mt: 1, mb: 2 }} />
            <TextField label="Location (optional)" fullWidth value={location} onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Building A, Room 302"
              sx={{}} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
            <Button variant="contained" onClick={createInspectionAndStart} disabled={!title.trim()} color="primary">
              Start Scanning
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Step 2: Active inspection — camera + results

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>Scan</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {captureCount} capture(s) &middot; {defects.length} defect(s) found
          </Typography>
        </Box>
        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={finishScan} sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
          Finish &amp; View Report
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* Camera / Preview column */}
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Video / captured image area */}
            <Box sx={{ position: 'relative', bgcolor: 'common.black', minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component="video"
                ref={videoRef}
                playsInline
                muted
                sx={{
                  width: '100%', maxHeight: 480, objectFit: 'contain',
                  display: cameraActive ? 'block' : 'none',
                }}
              />
              {capturedImage && !cameraActive && (
                <DefectOverlay src={capturedImage} detections={lastDetections} alt="Captured" />
              )}
              {!cameraActive && !capturedImage && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <CameraAltIcon sx={{ color: 'text.secondary', fontSize: 64, mb: 1 }} />
                          <Typography sx={{ color: 'text.secondary' }}>Camera is off</Typography>
                        </Box>
              )}
              {analysing && (
                <Box sx={{
                  position: 'absolute', inset: 0, bgcolor: alpha(theme.palette.common.black, 0.6),
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CircularProgress sx={{ color: theme.palette.primary.main, mb: 1 }} />
                  <Typography sx={{ color: 'text.primary', fontSize: 14 }}>Analyzing surface...</Typography>
                </Box>
              )}
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2, borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
              {cameraActive ? (
                <>
                  <Tooltip title="Flip Camera">
                    <IconButton onClick={flipCamera} sx={{ color: 'text.secondary', border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
                      <FlipCameraAndroidIcon />
                    </IconButton>
                  </Tooltip>
                  <Button variant="contained" startIcon={<CameraAltIcon />} onClick={capture}
                    disabled={analysing}
                    color="primary"
                    sx={{ px: 4, py: 1.2, fontSize: 15 }}>
                    Capture
                  </Button>
                  <Tooltip title="Stop Camera">
                    <IconButton onClick={stopCamera} sx={{ color: 'error.main', border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
                      <StopIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button variant="contained" startIcon={<CameraAltIcon />} onClick={() => startCamera()}
                    color="primary" sx={{ px: 3 }}>
                    {capturedImage ? 'Scan Again' : 'Start Camera'}
                  </Button>
                  <Box
                    {...getRootProps()}
                    role="button"
                    aria-label="Drop image or click to upload"
                    data-testid="scan-dropzone"
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 2, py: '7px',
                      border: `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.04)}`,
                      borderRadius: 1,
                      cursor: analysing ? 'not-allowed' : 'pointer',
                      color: isDragActive ? 'primary.light' : 'text.secondary',
                      bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input {...getInputProps()} data-testid="scan-dropzone-input" />
                    <UploadFileIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {isDragActive ? 'Drop image here' : 'Drop or click to upload'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Card>
          <Box component="canvas" ref={canvasRef} sx={{ display: 'none' }} />
        </Grid>

        {/* Results column */}
        <Grid item xs={12} md={5}>
          {/* Stats */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {[['Total', stats.total, theme.palette.primary.main], ['Critical', stats.critical, theme.palette.error.main], ['High', stats.high, theme.palette.warning.main], ['Confirmed', stats.confirmed, theme.palette.success.main]]
              .map(([label, val, color]) => (
                <Grid item xs={6} key={label as string}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ py: '8px !important', px: '12px !important' }}>
                      <Typography sx={{ color: 'text.secondary', fontSize: 11 }}>{label}</Typography>
                      <Typography sx={{ color: color as string, fontSize: 22, fontWeight: 'bold' }}>{val}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Latest capture results */}
          <LatestCaptureCard detections={lastDetections} />

          {/* Defects list */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1.5, fontSize: 14 }}>
                Detected Defects ({defects.length})
              </Typography>
              {defects.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>
                  No defects detected yet. Capture or upload an image to begin.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Type', 'Severity', 'Conf.', 'Actions'].map(h => (
                        <TableCell key={h} sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 11, px: 1 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {defects.map(defect => (
                      <TableRow key={defect.id} sx={{ '&:hover': { bgcolor: 'background.default' } }}>
                        <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontWeight: 600, fontSize: 12, px: 1 }}>
                          {defect.defect_type.toUpperCase()}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'divider', px: 1 }}>
                          <Chip label={defect.severity} size="small"
                            sx={{ ...getSeverityStyles(theme, defect.severity), fontWeight: 700, fontSize: 11 }} />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 12, px: 1 }}>
                          {defect.confidence != null ? `${Math.round(defect.confidence * 100)}%` : '-'}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'divider', px: 1 }}>
                          {!defect.is_confirmed && (
                            <Tooltip title="Confirm">
                              <IconButton size="small" onClick={() => confirmDefect(defect, true)} sx={{ color: 'success.main', p: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Reject">
                            <IconButton size="small" sx={{ color: 'error.main', p: 0.5 }}
                              onClick={() => { api.deleteDefect(defect.id).then(() => setDefects(prev => prev.filter(d => d.id !== defect.id))); }}>
                              <CancelIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
